import { useRef } from 'react';

import { useLaunchDarklyFlag } from '@/hooks/flags/useLaunchDarklyFlag';

import type { StandbyFlagGateStatus } from './types';
import { useIsStandbyFlagAnswered } from './useIsStandbyFlagAnswered';

export const STANDBY_FLAG = 'expo-flagship-standby';

/**
 * The single source of truth for the `expo-flagship-standby` kill switch,
 * extracted from `StandbyRegistrationScreen` so every screen the funnel can
 * be deep-linked into checks the same flag the same way. `loading` until
 * LaunchDarkly resolves (a cold start must wait rather than read the
 * caller's default as a real gate decision), then `open`/`closed`.
 */
export const useStandbyFlagGate = (): StandbyFlagGateStatus => {
  const isFlagResolved = useIsStandbyFlagAnswered(STANDBY_FLAG);
  const isStandbyEnabled = useLaunchDarklyFlag(STANDBY_FLAG, false);

  // The flag set can empty again while the LD client swaps context (see
  // useSettledFlagState), which reads as the `false` default while resolution
  // stays latched. An open gate therefore latches for the life of the mount —
  // same defense StandbyRegistrationScreen uses for its availability state —
  // so a transient fallback can't unmount an active funnel.
  const wasOpenRef = useRef(false);
  if (wasOpenRef.current) return 'open';

  if (!isFlagResolved) return 'loading';
  if (isStandbyEnabled) {
    wasOpenRef.current = true;
    return 'open';
  }
  return 'closed';
};
